import React, { useMemo, useState, useRef, useEffect } from "react";

const MUSCLE_FILTERS = [
	{ key: "all", label: "Tous", muscles: [] },
	{ key: "pecs", label: "Pecs", muscles: ["Pectoraux"] },
	{ key: "dos", label: "Dos", muscles: ["Dorsaux", "Trapezes"] },
	{ key: "jambes", label: "Jambes", muscles: ["Quadriceps", "Ischio-jambiers", "Mollets", "Fessiers", "Adducteurs", "Abducteurs"] },
	{ key: "bras", label: "Bras", muscles: ["Biceps", "Triceps", "Avant-bras"] },
	{ key: "epaules", label: "Épaules", muscles: ["Epaules"] },
	{ key: "abdos", label: "Abdos", muscles: ["Abdominaux", "Lombaires"] },
];

const MUSCLE_LABELS = [
	"Pectoraux", "Epaules", "Dorsaux", "Trapezes", "Biceps", "Triceps", "Avant-bras",
	"Cou", "Fessiers", "Quadriceps", "Ischio-jambiers", "Mollets", "Abdominaux",
	"Lombaires", "Adducteurs", "Abducteurs",
];

function getMuscleNames(muscles) {
	if (!muscles || muscles.length === 0) return [];
	return muscles
		.map((value, index) => (value === 1 ? MUSCLE_LABELS[index] : null))
		.filter(Boolean);
}

function normalize(s) {
	return (s || "").trim().toLowerCase();
}

function isAlreadyAdded(editorTraining, exerciseName) {
	if (!editorTraining || !editorTraining.exercices) return false;
	return editorTraining.exercices.some(
		(ex) => ex.supprime !== 1 && normalize(ex.nom) === normalize(exerciseName)
	);
}

function filterExercises(list, query, filterKey) {
	const q = normalize(query);
	const filterObj = MUSCLE_FILTERS.find((f) => f.key === filterKey);
	const muscleSet = new Set(filterObj?.muscles || []);
	return (list || []).filter((ex) => {
		if (q && !normalize(ex.nomExercice).includes(q)) return false;
		if (filterKey && filterKey !== "all" && muscleSet.size > 0) {
			const exMuscles = getMuscleNames(ex.muscles);
			if (!exMuscles.some((m) => muscleSet.has(m))) return false;
		}
		return true;
	});
}

export default function ExercisesPanel({
	exercises,
	loading,
	editorTraining,
	studentName,
	coachExercises = [],
	onAddExercise,
	onCreateNew,
	onImportFromLibrary,
	libraryMode = false,
}) {
	const [query, setQuery] = useState("");
	const [filter, setFilter] = useState("all");
	const [mode, setMode] = useState("list"); // "list" | "import"
	const [showNewMenu, setShowNewMenu] = useState(false);
	const [importQuery, setImportQuery] = useState("");
	const [importingId, setImportingId] = useState(null);
	const menuRef = useRef(null);

	useEffect(() => {
		if (!showNewMenu) return;
		const onClick = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) setShowNewMenu(false);
		};
		document.addEventListener("mousedown", onClick);
		return () => document.removeEventListener("mousedown", onClick);
	}, [showNewMenu]);

	const filtered = useMemo(
		() => filterExercises(exercises, query, filter),
		[exercises, query, filter]
	);

	// Coach library filtered by import search; mark those already in the student catalogue.
	const importList = useMemo(() => {
		const q = normalize(importQuery);
		const studentNames = new Set((exercises || []).map((e) => normalize(e.nomExercice)));
		return (coachExercises || [])
			.filter((ex) => !q || normalize(ex.nomExercice).includes(q))
			.map((ex) => ({ ...ex, _inStudent: studentNames.has(normalize(ex.nomExercice)) }));
	}, [coachExercises, importQuery, exercises]);

	const handleImport = async (ex) => {
		if (!onImportFromLibrary) return;
		setImportingId(ex.id);
		await onImportFromLibrary(ex);
		setImportingId(null);
	};

	// ===== Import sub-view =====
	if (mode === "import") {
		return (
			<div className="editor-panel-exercises">
				<button className="editor-panel-exercises-back" onClick={() => { setMode("list"); setImportQuery(""); }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
						<line x1="19" y1="12" x2="5" y2="12" />
						<polyline points="12 19 5 12 12 5" />
					</svg>
					Ma bibliothèque
				</button>

				<div className="editor-panel-exercises-subtitle">
					Importer dans le catalogue de {studentName || "l'élève"}
				</div>

				<div className="editor-panel-exercises-search">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input
						type="text"
						value={importQuery}
						onChange={(e) => setImportQuery(e.target.value)}
						placeholder="Rechercher dans ma bibliothèque"
						autoFocus
					/>
					{importQuery && (
						<button className="editor-panel-exercises-clear" onClick={() => setImportQuery("")}>×</button>
					)}
				</div>

				<div className="editor-panel-exercises-list">
					{importList.length === 0 ? (
						<div className="editor-panel-exercises-empty">Aucun exercice dans ta bibliothèque</div>
					) : (
						importList.map((ex) => {
							const muscles = getMuscleNames(ex.muscles);
							return (
								<button
									key={ex.id}
									className={`editor-panel-exercises-item ${ex._inStudent ? "added" : ""}`}
									onClick={() => !ex._inStudent && handleImport(ex)}
									disabled={ex._inStudent || importingId === ex.id}
								>
									<div className="editor-panel-exercises-item-info">
										<div className="editor-panel-exercises-item-name">{ex.nomExercice}</div>
										{muscles.length > 0 && (
											<div className="editor-panel-exercises-item-muscles">{muscles.slice(0, 3).join(" · ")}</div>
										)}
									</div>
									<span className={`editor-panel-exercises-item-status ${ex._inStudent ? "added" : ""}`}>
										{ex._inStudent ? "DÉJÀ PRÉSENT" : importingId === ex.id ? "…" : "Importer"}
									</span>
								</button>
							);
						})
					)}
				</div>
			</div>
		);
	}

	// ===== Main list view =====
	return (
		<div className="editor-panel-exercises">
			<div className="editor-panel-exercises-owner">
				{libraryMode ? <strong>Mes exercices</strong> : <>Exercices de <strong>{studentName || "l'élève"}</strong></>}
			</div>

			<div className="editor-panel-exercises-search">
				<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<circle cx="11" cy="11" r="8" />
					<line x1="21" y1="21" x2="16.65" y2="16.65" />
				</svg>
				<input
					type="text"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Rechercher un exercice"
					autoFocus
				/>
				{query && (
					<button className="editor-panel-exercises-clear" onClick={() => setQuery("")}>×</button>
				)}
			</div>

			<div className="editor-panel-exercises-count">
				{filtered.length} résultat{filtered.length > 1 ? "s" : ""}
			</div>

			<div className="editor-panel-exercises-filters">
				{MUSCLE_FILTERS.map((f) => (
					<button
						key={f.key}
						className={`editor-panel-exercises-pill ${filter === f.key ? "active" : ""}`}
						onClick={() => setFilter(f.key)}
					>
						{f.label}
					</button>
				))}
			</div>

			<div className="editor-panel-exercises-newbar" ref={menuRef}>
				<button
					className="editor-panel-exercises-new-btn"
					onClick={() => (libraryMode ? onCreateNew?.() : setShowNewMenu((v) => !v))}
				>
					+ Nouvel exercice
				</button>
				{!libraryMode && showNewMenu && (
					<div className="editor-panel-exercises-new-menu top">
						<button
							className="editor-panel-exercises-new-menu-item"
							onClick={() => { setShowNewMenu(false); setMode("import"); }}
						>
							<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
								<polyline points="7 10 12 15 17 10" />
								<line x1="12" y1="15" x2="12" y2="3" />
							</svg>
							<div className="editor-panel-exercises-new-menu-text">
								<span>Importer depuis ma bibliothèque</span>
								<span className="sub">Réutiliser un de tes exercices</span>
							</div>
						</button>
						<button
							className="editor-panel-exercises-new-menu-item"
							onClick={() => { setShowNewMenu(false); onCreateNew?.(); }}
						>
							<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<line x1="12" y1="5" x2="12" y2="19" />
								<line x1="5" y1="12" x2="19" y2="12" />
							</svg>
							<div className="editor-panel-exercises-new-menu-text">
								<span>Créer un nouvel exercice</span>
								<span className="sub">From scratch</span>
							</div>
						</button>
					</div>
				)}
			</div>

			<div className="editor-panel-exercises-list">
				{loading ? (
					<div className="editor-panel-exercises-empty">Chargement…</div>
				) : filtered.length === 0 ? (
					<div className="editor-panel-exercises-empty">Aucun exercice trouvé</div>
				) : (
					filtered.map((ex) => {
						const added = isAlreadyAdded(editorTraining, ex.nomExercice);
						const muscles = getMuscleNames(ex.muscles);
						return (
							<button
								key={ex.id}
								className={`editor-panel-exercises-item ${added ? "added" : ""}`}
								onClick={() => onAddExercise?.(ex)}
							>
								<div className="editor-panel-exercises-item-info">
									<div className="editor-panel-exercises-item-name">{ex.nomExercice}</div>
									{muscles.length > 0 && (
										<div className="editor-panel-exercises-item-muscles">
											{muscles.slice(0, 3).join(" · ")}
										</div>
									)}
								</div>
								<span className={`editor-panel-exercises-item-status ${added ? "added" : ""}`}>
									{added ? "AJOUTÉ" : "+"}
								</span>
							</button>
						);
					})
				)}
			</div>

		</div>
	);
}
